/**
 * Pomodoro Timer Local Notifications
 *
 * Schedules local notifications for when the timer completes,
 * so users get notified even if the app is in the background.
 *
 * Android: Uses the native "pomodoro_alarm" channel created in MainActivity.java
 *   - AudioAttributes.USAGE_ALARM  → plays on alarm stream (bypasses silent/ring volume)
 *   - setBypassDnd(true)           → breaks through Do Not Disturb
 *   - IMPORTANCE_HIGH              → heads-up notification
 *
 * iOS: Uses TimeSensitiveNotificationPlugin to schedule with interruptionLevel
 *   .timeSensitive so notifications break through Do Not Disturb and reach the watch.
 *   The AlarmSoundPlugin handles foreground playback that bypasses the mute switch.
 */

import { Capacitor, registerPlugin } from '@capacitor/core'
import type { ScheduleOptions, ActionPerformed } from '@capacitor/local-notifications'

type LNPlugin = typeof import('@capacitor/local-notifications').LocalNotifications

/** Time-Sensitive notification plugin (iOS only) – breaks through DND and reaches watch */
interface TimeSensitiveNotificationPlugin {
  schedule(options: {
    id: number
    title: string
    body: string
    triggerAt: number
    sound?: string
    extra?: Record<string, string>
  }): Promise<{ scheduled: boolean }>
  cancel(options: { ids: number[] }): Promise<{ cancelled: boolean }>
}

let _cachedTSNResult: { plugin: TimeSensitiveNotificationPlugin } | null | undefined

async function getTimeSensitiveNotificationPlugin(): Promise<{ plugin: TimeSensitiveNotificationPlugin } | null> {
  if (_cachedTSNResult !== undefined) return _cachedTSNResult
  if (Capacitor.getPlatform() !== 'ios') {
    _cachedTSNResult = null
    return null
  }
  try {
    const plugin = registerPlugin<TimeSensitiveNotificationPlugin>('TimeSensitiveNotificationPlugin')
    _cachedTSNResult = { plugin }
    return _cachedTSNResult
  } catch (e) {
    console.warn('[PomodoroNotifications] TimeSensitiveNotificationPlugin unavailable:', e)
    _cachedTSNResult = null
    return null
  }
}

/**
 * Lazy-load the plugin wrapped in a plain object.
 *
 * Capacitor plugins are Proxy objects that intercept ALL property access.
 * Wrapping in `{ plugin }` breaks the thenable chain so `await` resolves
 * to the plain wrapper object instead of calling `plugin.then()`.
 */
let _cachedLNResult: { plugin: LNPlugin } | null | undefined

async function getLocalNotifications(): Promise<{ plugin: LNPlugin } | null> {
  if (_cachedLNResult !== undefined) return _cachedLNResult
  try {
    const { LocalNotifications } = await import('@capacitor/local-notifications')
    _cachedLNResult = { plugin: LocalNotifications }
  } catch (e) {
    console.warn('[PomodoroNotifications] LocalNotifications plugin unavailable:', e)
    _cachedLNResult = null
  }
  return _cachedLNResult
}

const NOTIFICATION_IDS = {
  work: 1001,
  short_break: 1002,
  long_break: 1003,
}

// The alarm-level channel is created in native Java (MainActivity.java).
// It uses AudioAttributes.USAGE_ALARM + bypassDnd so it rings even on silent.
const ANDROID_ALARM_CHANNEL_ID = 'pomodoro_alarm'

export type PomodoroNotificationType = 'work' | 'short_break' | 'long_break'

type NotificationTapPayload = { sessionType: PomodoroNotificationType; action: 'completed' }
type NotificationTapCallback = (data: NotificationTapPayload) => void
let notificationTapCallback: NotificationTapCallback | null = null

type NavigateFunction = (path: string) => void
let navigateFunction: NavigateFunction | null = null

let listenersInitialized = false

// If a notification tap arrives before PomodoroPage sets its callback,
// store it here so it can be delivered as soon as the callback is set.
let pendingNotificationTap: NotificationTapPayload | null = null

/**
 * Set the callback for handling notification taps.
 * If a tap arrived while the callback was null, it is delivered immediately.
 */
export function setNotificationTapCallback(callback: NotificationTapCallback | null): void {
  notificationTapCallback = callback

  if (callback && pendingNotificationTap) {
    const pending = pendingNotificationTap
    pendingNotificationTap = null
    callback(pending)
  }
}

/**
 * Set the navigation function for handling notification tap navigation
 */
export function setPomodoroNotificationNavigate(navigate: NavigateFunction | null): void {
  navigateFunction = navigate
}

/**
 * Deliver (or queue) a notification tap event.
 * Navigation happens immediately; the completion callback is either
 * dispatched now or stored for when PomodoroPage registers its callback.
 */
function handleNotificationTapEvent(extra: { sessionType: PomodoroNotificationType; action: string }): void {
  if (extra.action !== 'completed' || !extra.sessionType) return

  if (navigateFunction) {
    console.log('[PomodoroNotifications] Navigating to /pomodoro from notification tap')
    navigateFunction('/pomodoro')
  }

  const payload: NotificationTapPayload = {
    sessionType: extra.sessionType,
    action: 'completed',
  }

  if (notificationTapCallback) {
    notificationTapCallback(payload)
  } else {
    pendingNotificationTap = payload
    console.log('[PomodoroNotifications] Callback not set, queued tap for later delivery')
  }
}

/**
 * Initialize notification listeners.
 * Idempotent -- safe to call multiple times; only registers once.
 */
export async function initPomodoroNotificationListeners(): Promise<void> {
  if (!Capacitor.isNativePlatform() || listenersInitialized) {
    return
  }
  listenersInitialized = true

  try {
    const result = await getLocalNotifications()
    if (!result) return
    const LocalNotifications = result.plugin

    await LocalNotifications.addListener('localNotificationActionPerformed', (action: ActionPerformed) => {
      console.log('[PomodoroNotifications] Notification tapped:', action)
      const extra = action.notification.extra as { sessionType?: PomodoroNotificationType; action?: string } | undefined
      if (extra?.action && extra?.sessionType) {
        handleNotificationTapEvent(extra as { sessionType: PomodoroNotificationType; action: string })
      }
    })

    await LocalNotifications.addListener('localNotificationReceived', (notification) => {
      console.log('[PomodoroNotifications] Notification received in foreground:', notification)
    })

    console.log('[PomodoroNotifications] Notification listeners initialized')
  } catch (error) {
    console.warn('[PomodoroNotifications] Failed to initialize notification listeners:', error)
  }
}

/**
 * Request permission for local notifications
 */
export async function requestNotificationPermission(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) {
    return false
  }

  try {
    const result = await getLocalNotifications()
    if (!result) return false
    const permission = await result.plugin.requestPermissions()
    return permission.display === 'granted'
  } catch (error) {
    console.warn('Failed to request notification permission:', error)
    return false
  }
}

/**
 * Check and request exact alarm permission on Android 12+.
 * Returns true if exact alarms are available.
 */
async function ensureExactAlarmPermission(): Promise<boolean> {
  if (Capacitor.getPlatform() !== 'android') return true

  const result = await getLocalNotifications()
  if (!result) return false
  const LN = result.plugin

  try {
    const setting = await LN.checkExactNotificationSetting()
    if (setting.exact_alarm === 'granted') return true

    console.warn('[PomodoroNotifications] Exact alarm not granted, opening settings')
    const changed = await LN.changeExactNotificationSetting()
    return changed.exact_alarm === 'granted'
  } catch {
    // checkExactNotificationSetting not available (older plugin or Android < 12)
    return true
  }
}

/**
 * Schedule a local notification for when the pomodoro timer ends.
 *
 * On Android, uses the "pomodoro_alarm" channel (created natively in MainActivity.java)
 * which has USAGE_ALARM + bypassDnd -- it will ring even on silent/DND.
 *
 * On iOS, uses TimeSensitiveNotificationPlugin so the notification breaks through
 * Do Not Disturb and is delivered to the Apple Watch.
 */
export async function schedulePomodoroNotification(
  sessionType: PomodoroNotificationType,
  durationSeconds: number,
  isLongBreakNext: boolean = false
): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    console.log('[PomodoroNotifications] Not native platform, skipping')
    return
  }

  if (durationSeconds <= 0) {
    console.warn('[PomodoroNotifications] Duration must be > 0, got:', durationSeconds)
    return
  }

  const triggerAt = new Date(Date.now() + (durationSeconds * 1000) + 100)

  let title: string
  let body: string
  let iosSound: string
  let notificationId: number

  if (sessionType === 'work') {
    if (isLongBreakNext) {
      title = '🎉 Great work!'
      body = 'You earned a long break. Time to recharge!'
      iosSound = 'gong.wav'
      notificationId = NOTIFICATION_IDS.long_break
    } else {
      title = '✨ Session complete!'
      body = 'Time for a short break.'
      iosSound = 'g_blow.wav'
      notificationId = NOTIFICATION_IDS.short_break
    }
  } else {
    title = '🎯 Break over'
    body = 'Ready to focus again?'
    iosSound = 'typewriter.wav'
    notificationId = NOTIFICATION_IDS.work
  }

  const isAndroid = Capacitor.getPlatform() === 'android'

  if (isAndroid) {
    try {
      const lnResult = await getLocalNotifications()
      if (!lnResult) return
      const LocalNotifications = lnResult.plugin

      const permResult = await LocalNotifications.checkPermissions()
      if (permResult.display !== 'granted') {
        console.warn('[PomodoroNotifications] Notification permission not granted:', permResult.display)
        const reqResult = await LocalNotifications.requestPermissions()
        if (reqResult.display !== 'granted') {
          console.error('[PomodoroNotifications] Permission denied after request')
          return
        }
      }

      await ensureExactAlarmPermission()

      await LocalNotifications.cancel({
        notifications: [{ id: notificationId }]
      }).catch(() => {})

      const options: ScheduleOptions = {
        notifications: [
          {
            id: notificationId,
            title,
            body,
            schedule: {
              at: triggerAt,
              allowWhileIdle: true,
            },
            sound: iosSound,
            threadIdentifier: 'pomodoro',
            channelId: ANDROID_ALARM_CHANNEL_ID,
            extra: {
              sessionType,
              action: 'completed',
            },
          },
        ],
      }

      await LocalNotifications.schedule(options)
      console.log(`[PomodoroNotifications] Scheduled (Android) for ${triggerAt.toISOString()}, id: ${notificationId}`)
    } catch (error) {
      console.error('[PomodoroNotifications] Failed to schedule notification:', error)
    }
    return
  }

  // iOS: use Time-Sensitive native plugin so it breaks through DND and reaches the watch
  try {
    const tsn = await getTimeSensitiveNotificationPlugin()
    if (!tsn) {
      // Fallback to standard local notifications if plugin unavailable
      await schedulePomodoroNotificationViaLocalNotifications(
        sessionType,
        durationSeconds,
        isLongBreakNext,
        title,
        body,
        iosSound,
        notificationId,
        triggerAt
      )
      return
    }

    const permResult = await (await getLocalNotifications())?.plugin.checkPermissions()
    if (permResult?.display !== 'granted') {
      const reqResult = await (await getLocalNotifications())?.plugin.requestPermissions()
      if (reqResult?.display !== 'granted') {
        console.error('[PomodoroNotifications] Permission denied after request')
        return
      }
    }

    await tsn.plugin.cancel({ ids: [notificationId] }).catch(() => {})

    await tsn.plugin.schedule({
      id: notificationId,
      title,
      body,
      triggerAt: triggerAt.getTime(),
      sound: iosSound,
      extra: { sessionType, action: 'completed' },
    })
    console.log(`[PomodoroNotifications] Scheduled (iOS Time-Sensitive) for ${triggerAt.toISOString()}, id: ${notificationId}`)
  } catch (error) {
    console.error('[PomodoroNotifications] Failed to schedule iOS time-sensitive notification, falling back:', error)
    await schedulePomodoroNotificationViaLocalNotifications(
      sessionType,
      durationSeconds,
      isLongBreakNext,
      title,
      body,
      iosSound,
      notificationId,
      triggerAt
    )
  }
}

async function schedulePomodoroNotificationViaLocalNotifications(
  sessionType: PomodoroNotificationType,
  durationSeconds: number,
  isLongBreakNext: boolean,
  title: string,
  body: string,
  iosSound: string,
  notificationId: number,
  triggerAt: Date
): Promise<void> {
  const lnResult = await getLocalNotifications()
  if (!lnResult) return
  const LocalNotifications = lnResult.plugin

  const permResult = await LocalNotifications.checkPermissions()
  if (permResult.display !== 'granted') {
    const reqResult = await LocalNotifications.requestPermissions()
    if (reqResult.display !== 'granted') return
  }

  await LocalNotifications.cancel({ notifications: [{ id: notificationId }] }).catch(() => {})

  const options: ScheduleOptions = {
    notifications: [
      {
        id: notificationId,
        title,
        body,
        schedule: { at: triggerAt, allowWhileIdle: true },
        sound: iosSound,
        threadIdentifier: 'pomodoro',
        extra: { sessionType, action: 'completed' },
      },
    ],
  }
  await LocalNotifications.schedule(options)
  console.log(`[PomodoroNotifications] Scheduled (iOS fallback) for ${triggerAt.toISOString()}, id: ${notificationId}`)
}

/**
 * Cancel any pending pomodoro notifications.
 * Call this when the timer is stopped manually or completes while app is in foreground.
 */
export async function cancelPomodoroNotifications(): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    return
  }

  const ids = [NOTIFICATION_IDS.work, NOTIFICATION_IDS.short_break, NOTIFICATION_IDS.long_break]

  if (Capacitor.getPlatform() === 'ios') {
    try {
      const tsn = await getTimeSensitiveNotificationPlugin()
      if (tsn) {
        await tsn.plugin.cancel({ ids })
      }
    } catch (e) {
      // ignore
    }
  }

  try {
    const cancelResult = await getLocalNotifications()
    if (!cancelResult) return
    await cancelResult.plugin.cancel({
      notifications: ids.map((id) => ({ id })),
    })
    console.debug('Cancelled pomodoro notifications')
  } catch (error) {
    console.warn('Failed to cancel pomodoro notifications:', error)
  }
}

/**
 * No-op on current version -- channels are created natively in MainActivity.java.
 * Kept for backward compatibility with callers.
 */
export async function createPomodoroNotificationChannel(): Promise<void> {
  // Channel creation is handled in native Java code (MainActivity.java)
  // with alarm-level AudioAttributes that can't be set from JS.
}
